import {
  Component,
  effect,
  inject,
  OnInit,
  OnDestroy,
  signal,
  Signal,
} from '@angular/core';

import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import { fromEvent, filter, merge } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, throttleTime } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';

import { ApplicationConfig } from '../../config/application.config';
import { AuthFacadeService } from '../../../domain/auth/service/auth-facade.service';
import { ApplicationSelectors } from '../../../shared/store/application/application.selectors';
import { AuthSelectors } from '../../../shared/store/auth/auth.selectors';
import { SessionTimeoutService } from '../../service/session-timeout.service';
import { TokenRefreshSchedulerService } from '../../service/token-refresh-scheduler.service';
import { LayoutService } from '../../service/layout.service';
import { UserType } from '../../../domain/user/model/user-type';
import { NgIcon } from '@ng-icons/core';
import { MenuComponent, AppMenuItem } from './container/menu/menu.component';
import { LanguageSwitcherComponent } from '../preliminary-layout/language-switcher/language-switcher.component';
import { UserMenuComponent } from './container/user-menu/user-menu.component';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    RouterLink,
    RouterOutlet,
    TranslateModule,
    NgIcon,
    MenuComponent,
    LanguageSwitcherComponent,
    UserMenuComponent,
  ],
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss',
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private store = inject(Store);
  private sessionTimeoutService = inject(SessionTimeoutService);
  private tokenRefreshScheduler = inject(TokenRefreshSchedulerService);
  public authFacadeService = inject(AuthFacadeService);

  public pageHeader = this.store.selectSignal(ApplicationSelectors.pageHeader);
  public layoutFullscreen: Signal<boolean>;
  private account = this.store.selectSignal(AuthSelectors.getCurrentLogged);
  private readonly layoutService = inject(LayoutService);

  public flushLayout = signal(false);
  public mobileMenuOpen = false;
  public versionMouseOver = false;
  public devMode = !ApplicationConfig.production;
  public applicationVersion = ApplicationConfig.version;
  public environmentName = ApplicationConfig.environmentName ?? '';

  public menuItems: AppMenuItem[] = [];

  constructor() {
    this.layoutFullscreen = this.layoutService.fullscreen;

    effect(() => {
      const userType = this.account()?.userType;
      this.menuItems = this.buildMenuItems(userType);
    });

    this.flushLayout.set(this.checkFlushLayout());

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map(() => this.checkFlushLayout()),
        takeUntilDestroyed()
      )
      .subscribe(flush => this.flushLayout.set(flush));

    merge(
      fromEvent(document, 'click'),
      fromEvent(document, 'keydown'),
      this.router.events
    )
      .pipe(throttleTime(1000), takeUntilDestroyed())
      .subscribe(() => this.sessionTimeoutService.reset());
  }

  ngOnInit(): void {
    this.sessionTimeoutService.start();
    this.tokenRefreshScheduler.start();
  }

  ngOnDestroy(): void {
    this.sessionTimeoutService.stop();
    this.tokenRefreshScheduler.stop();
  }

  private checkFlushLayout(): boolean {
    let route: ActivatedRoute | null = this.activatedRoute;
    while (route) {
      if (route.snapshot?.data?.['flushLayout']) return true;
      route = route.firstChild;
    }
    return false;
  }

  private buildMenuItems(userType: UserType | undefined): AppMenuItem[] {
    const items: AppMenuItem[] = [
      // TODO: Re-enable when needed
      // {
      //   labelKey: 'NAVIGATION.MESSAGES',
      //   icon: 'bootstrapChatDots',
      //   routerLink: ['/messages'],
      // },

      {
        labelKey: 'NAVIGATION.MEDICAL_CASES',
        icon: 'bootstrapList',
        routerLink: ['/medical-cases'],
      },

      // items.push({
      //   labelKey: 'NAVIGATION.SETTINGS',
      //   icon: 'bootstrapGear',
      //   routerLink: ['/settings'],
      // });
    ];
    if (userType === UserType.ADMIN) {
      items.push({
        labelKey: 'NAVIGATION.USERS',
        icon: 'bootstrapPeople',
        routerLink: ['/users'],
      });
    }
    return items;
  }
}
